import { useState, useRef } from "react";
import { api, imgUrl, SWATCH } from "../lib/api";
import { toast } from "sonner";
import { X, UploadCloud, Trash2, Star, Loader2, ImageOff } from "lucide-react";

const COLOR_OPTIONS = ["auto", "Gold", "Rose Gold", "Silver"];

export function ImageManager({ product, onUpdated, onClose }) {
  const [p, setP] = useState(product);
  const [color, setColor] = useState("auto");
  const [progress, setProgress] = useState(null); // {done, total}
  const [busyPath, setBusyPath] = useState(null);
  const fileRef = useRef(null);

  const sync = (data) => {
    setP(data);
    onUpdated(data);
  };

  const onFiles = async (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    setProgress({ done: 0, total: files.length });
    let latest = p;
    for (let i = 0; i < files.length; i++) {
      const fd = new FormData();
      fd.append("file", files[i]);
      fd.append("color", color);
      try {
        const { data } = await api.post(`/admin/products/${p.id}/images`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        latest = data.product;
        setP(latest);
      } catch (e) {
        toast(`"${files[i].name}" failed to upload.`);
      }
      setProgress({ done: i + 1, total: files.length });
    }
    onUpdated(latest);
    setProgress(null);
    if (fileRef.current) fileRef.current.value = "";
    toast(`${files.length} image${files.length > 1 ? "s" : ""} uploaded.`);
  };

  const removeImage = async (path) => {
    setBusyPath(path);
    try {
      const { data } = await api.delete(`/admin/products/${p.id}/images`, { data: { color: "", path } });
      sync(data);
      toast("Image removed.");
    } catch {
      toast("Could not remove image.");
    }
    setBusyPath(null);
  };

  const setPrimary = async (variantColor, path) => {
    setBusyPath(path);
    try {
      const { data } = await api.post(`/admin/products/${p.id}/images/primary`, { color: variantColor, path });
      sync(data);
      toast("Set as hero image.");
    } catch {
      toast("Could not update.");
    }
    setBusyPath(null);
  };

  const totalImages = (p.variants || []).reduce((n, v) => n + v.images.length, 0);

  return (
    <div
      data-testid="image-manager-modal"
      className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
      onClick={onClose}
    >
      <div
        className="bg-[#F7F7F9] border border-[#E5E5E5] w-full max-w-3xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex items-center justify-between px-7 py-5 border-b border-[#E5E5E5] sticky top-0 bg-[#F7F7F9] z-10">
          <div>
            <p className="label-track text-[#0A0A0A] text-[10px]">Image Studio</p>
            <h3 className="font-heading text-2xl text-[#0A0A0A] mt-1">{p.name}</h3>
            <p className="text-[10px] text-[#737373] font-mono mt-1">
              {p.category_label} · {p.product_code} · {totalImages} image{totalImages !== 1 ? "s" : ""}
            </p>
          </div>
          <button data-testid="image-manager-close" onClick={onClose} className="text-[#737373] hover:text-[#0A0A0A]">
            <X size={22} />
          </button>
        </div>

        <div className="p-7 space-y-8">
          {/* upload zone */}
          <div>
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <span className="label-track text-[#737373] text-[10px]">Assign color</span>
              <div className="flex gap-2">
                {COLOR_OPTIONS.map((c) => (
                  <button
                    key={c}
                    data-testid={`upload-color-${c.replace(/\s/g, "-").toLowerCase()}`}
                    onClick={() => setColor(c)}
                    className={`px-3 py-1 text-xs label-track border transition-colors ${
                      color === c ? "border-[#0A0A0A] text-[#0A0A0A] bg-[#F7F7F9]" : "border-[#E5E5E5] text-[#737373]"
                    }`}
                  >
                    {c === "auto" ? "Auto-detect" : c}
                  </button>
                ))}
              </div>
            </div>
            <div
              data-testid="upload-dropzone"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                onFiles(e.dataTransfer.files);
              }}
              className="border-2 border-dashed border-[#D8CEBE] hover:border-[#0A0A0A] cursor-pointer rounded-sm py-10 flex flex-col items-center justify-center text-center transition-colors"
            >
              {progress ? (
                <>
                  <Loader2 size={26} className="text-[#0A0A0A] animate-spin mb-2" />
                  <p className="text-sm text-[#0A0A0A]">Uploading {progress.done}/{progress.total}…</p>
                </>
              ) : (
                <>
                  <UploadCloud size={26} className="text-[#0A0A0A] mb-2" />
                  <p className="text-sm text-[#0A0A0A]">Drag &amp; drop images, or click to browse</p>
                  <p className="text-[11px] text-[#737373] mt-1">
                    Each image is auto-trimmed onto a clean white background.
                    {color === "auto" ? " Color is detected automatically." : ` Added to ${color}.`}
                  </p>
                </>
              )}
              <input
                ref={fileRef}
                data-testid="upload-file-input"
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => onFiles(e.target.files)}
              />
            </div>
          </div>

          {/* variants */}
          {totalImages === 0 && (
            <div className="flex flex-col items-center text-center py-8 text-[#737373]">
              <ImageOff size={26} className="mb-2 opacity-60" />
              <p className="text-sm">No images yet. Upload some above.</p>
            </div>
          )}

          {(p.variants || []).map((v) => (
            <div key={v.color} data-testid={`variant-section-${v.color.replace(/\s/g, "-").toLowerCase()}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-3 h-3 rounded-full border border-[#E5E5E5]" style={{ background: SWATCH[v.color] || "#ccc" }} />
                <span className="label-track text-[#0A0A0A] text-xs">{v.color}</span>
                <span className="text-[10px] text-[#737373]">({v.images.length})</span>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {v.images.map((path, idx) => (
                  <div
                    key={path}
                    data-testid={`managed-image-${v.color.replace(/\s/g, "-").toLowerCase()}-${idx}`}
                    className="relative group aspect-square bg-white border border-[#E5E5E5] overflow-hidden"
                  >
                    <img src={imgUrl(path)} alt="" className="w-full h-full object-contain" />
                    {idx === 0 && (
                      <span className="absolute top-1 left-1 bg-[#0A0A0A] text-white text-[8px] px-1.5 py-0.5 label-track">
                        HERO
                      </span>
                    )}
                    <div className="absolute inset-0 bg-black/40 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      {idx !== 0 && (
                        <button
                          data-testid={`set-primary-${v.color.replace(/\s/g, "-").toLowerCase()}-${idx}`}
                          onClick={() => setPrimary(v.color, path)}
                          disabled={busyPath === path}
                          title="Set as hero"
                          className="bg-white/90 text-[#0A0A0A] p-2 rounded-full hover:bg-white disabled:opacity-50"
                        >
                          <Star size={14} />
                        </button>
                      )}
                      <button
                        data-testid={`delete-image-${v.color.replace(/\s/g, "-").toLowerCase()}-${idx}`}
                        onClick={() => removeImage(path)}
                        disabled={busyPath === path}
                        title="Remove image"
                        className="bg-white/90 text-[#B76E79] p-2 rounded-full hover:bg-white disabled:opacity-50"
                      >
                        {busyPath === path ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
